import { HttpHeaders } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Data, ParamMap, Router } from '@angular/router';
import { ConfirmationService, Message } from 'primeng/api';
import { AutoComplete } from 'primeng/autocomplete';
import { FileRemoveEvent, FileUpload, FileUploadErrorEvent, FileUploadEvent } from 'primeng/fileupload';
import { forkJoin, Observable, Subject, take, takeUntil } from 'rxjs';
import { AccountComponent } from 'src/app/account/account.component';
import { AuthService } from 'src/app/auth/auth.service';
import { AuthRoles } from 'src/app/auth/auth.types';
import { AccessibilityService } from 'src/app/services/accessibility.service';
import { AgencyService } from 'src/app/services/agency.service';
import { CrimeGunService } from 'src/app/services/crime-gun.service';
import { LeCaseNumberService } from 'src/app/services/le-case-number.service';
import { LoaderService } from 'src/app/services/loader.service';
import { LocationService } from 'src/app/services/location.service';
import { LookupService } from 'src/app/services/lookup.service';
import { PeopleService } from 'src/app/services/people.service';
import { PreviousRouteService } from 'src/app/services/previous-route.service';
import { ProductsListService } from 'src/app/services/products-list.service';
import { ResultsService } from 'src/app/services/results.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { NotificationType } from 'src/app/shared/app.enums';
import { AppPermissions } from 'src/app/shared/app.permissions';
import { AppUtils } from 'src/app/shared/app.utils';
import { ResultNotification } from 'src/app/shared/models/result-notification';
import { NESSValidators } from 'src/app/shared/validators/ness.validators';
import { environment } from 'src/environments/environment';
import { LoginService } from '../../services/login.service';
import { TabViewChangeEvent } from 'primeng/tabview';

@Component({
  selector: 'ness-results-form',
  templateUrl: './results-form.component.html',
  providers: [ConfirmationService]
})
export class ResultsFormComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('downloadTemplateLink', { static: false }) private downloadTemplateLink: ElementRef;
  @ViewChild('fileInput') fileInput: FileUpload;
  appConstants = AppConstants;
  agenciesByStateId: ServerModels.ENESS_REF_Agency[] = [];
  allowedFileTypes: string = '.pdf,.PDF,.jpg,.JPG,.png,.PNG,.tiff,.TIFF,.jpeg,.JPEG,.anb,.ANB,.doc,.DOC,.docx,.DOCX,.xls,.XLS,.xlsx,.XLSX,.ppt,.PPT,.pptx,.PPTX,.msg,.MSG';
  apiRoot: string = environment.apiEndpoint;
  approvedStatusId: string;
  attachmentUploadErrorMessage: string;
  canEditFD: AuthRoles[] = ['NESS Administrator'];
  caseNumbersByAgencyId: ServerModels.ENESS_XREF_LECaseNumbers[] = [];
  caseNumbersTextFiltered: ServerModels.ENESS_XREF_LECaseNumbers[] = [];
  crimeGunNumbers: ServerModels.CrimeGunSelectionValues[] = [];
  displayAttachmentErrorMessage: boolean = false;
  fieldDivisions$: Observable<ServerModels.ENESS_REF_FieldDivisions[]> = this.loginService.fieldDivisions$;
  formTitle: string = '';
  hideNFORCECaseInput: boolean;
  irTrackingNumbers: ServerModels.CIPSelectionValues[] = [];
  leCaseNumberForm: FormGroup = this.fb.group({
    StateID: ['', Validators.required],
    AgencyID: [{ value: '', disabled: true }, Validators.required],
    LECaseNumberID: [{ value: '', disabled: true }, Validators.required]
  }, { validator: NESSValidators.requireDisabled });
  rejectedStatusId: string;
  resultData: ServerModels.ResultDetailViewModel;
  currentResultStatus: ServerModels.ENESS_XREF_SuccessStoryStatus = null;
  newResultStatus: ServerModels.ENESS_XREF_SuccessStoryStatus = null;
  totalNumberOfUploadFilesAllowed: number = 50;
  resultForm: FormGroup = this.fb.group({
    SuccessStoryID: '',
    SuccessStoryStatusID: ['', Validators.required],
    InvestigativeTrackingNumber: [{ value: '', disabled: true }],
    Supervisor: '',
    CGICSupervisor_Id: [{ value: '', disabled: false }, Validators.required],
    AlternateCGICApprover_Id: null,
    Narrative: ['', Validators.required],
    StatusNotes: ['', Validators.maxLength(2000)],
    NFORCECaseNumber: '',
    HasNFORCECaseNumberID: ['', Validators.required],
    ENESS_XREF_SuccessStoriesATFAssets: this.fb.array([]),
    ENESS_XREF_SuccessStoriesFactors: this.fb.array([], NESSValidators.multipleCheckboxRequireOne),
    ENESS_XREF_SuccessStoriesCIPs: this.fb.array([]),
    ENESS_XREF_SuccessStoriesCrimeGuns_Primary: this.fb.array([]),
    ENESS_XREF_SuccessStoriesCrimeGuns_Related: this.fb.array([]),
    ENESS_XREF_SuccessStoriesLECaseNumbers: this.fb.array([]),
    ENESS_XREF_SuccessStoriesAttachments: this.fb.array([], NESSValidators.formArrayMaxLength(this.totalNumberOfUploadFilesAllowed)),
    ENESS_XREF_SuccessStoriesDefendants: this.fb.array([]),
    NumberOfShootings: [null, Validators.required],
    NumberOfFirearmsRecovered: [null, Validators.required],
    CGICCode: [{ value: '', disabled: !this.authService.hasRole(this.canEditFD) }, Validators.required],
    CreatorName: [{ value: '', disabled: true }, Validators.required],
    CreatedBy: '',
    CreatedBy_Id: '',
    CreatedOn: ''
  });
  rows: number = AppConstants.defaultTableRows;
  selectedDefendant: ServerModels.ENESS_XREF_SuccessStoriesDefendants = null;
  selectedPerson: ServerModels.ENESS_PersonDTO = null;
  showApprovalTab: boolean = false;
  showPerson: boolean = false;
  showSidebar: boolean = false;
  maxFileSize: number = environment.maxUploadFileSizeInBytes; //5mb
  textAreaRows: number = AppConstants.defaultTextAreaRows;
  generalNotification: ResultNotification = { header: 'General information', message: '', NotificationType: NotificationType.default, TabIndex: 0 };
  narrativeNotification: ResultNotification = { header: 'Narrative/Attachments information', message: '', NotificationType: NotificationType.default, TabIndex: 1 };
  defendantsNotification: ResultNotification = { header: 'Defendant(s) information', message: '', NotificationType: NotificationType.default, TabIndex: 2 };
  fileUploadHeaders: HttpHeaders = new HttpHeaders({
    'Authorization': 'Bearer ' + sessionStorage.getItem(environment.authTokenStorageKey),
    'Skip-Loader': 'true'
  });
  hasPermissionToEditAddress: boolean = false;
  personAddressType: string;
  personAddressTypeFromRef: string;
  personAddressTypeOther: string;
  viewPersonDetails: boolean = false;
  showPersonAddressDetails: boolean = false;
  showPersonAddressDetailForm: boolean = false;
  showLocationView: boolean = false;
  showEditLocationView: boolean = false;
  personAddress: ServerModels.ENESS_AddressDTO;
  approveOrRejected: string = "";
  approvalFormTitleText: string = null;
  msgs: Message[] = [];

  private _resultId: string = null;
  private _returnUrl: string = this.previousRouteService.getReturnUrl();
  private _unsubscribe$: Subject<void> = new Subject<void>();

  private _activeTabIndex: number = 0;
  get activeTabIndex(): number {
    return this._activeTabIndex;
  }

  set activeTabIndex(idx: number) {
    this._activeTabIndex = idx;
  }

  private _cgicCode: string;
  set cgicCode(code: string) {
    this._cgicCode = code;
  }

  get cgicCode(): string {
    return this._cgicCode;
  }

  get ENESS_REF_SuccessStoriesStatuses(): ServerModels.ENESS_REF_SuccessStoriesStatuses {
    return this.resultData.ENESS_SuccessStory?.CurrentSuccessStoryStatus?.ENESS_REF_SuccessStoriesStatuses;
  }

  get ENESS_XREF_SuccessStoriesFactors(): FormArray {

    return this.resultForm.get('ENESS_XREF_SuccessStoriesFactors') as FormArray;
  }

  get ENESS_XREF_SuccessStoriesLECaseNumbers(): FormArray {
    return this.resultForm.get('ENESS_XREF_SuccessStoriesLECaseNumbers') as FormArray;
  }

  get ENESS_XREF_SuccessStoriesCrimeGuns_Primary(): FormArray {
    return this.resultForm.get('ENESS_XREF_SuccessStoriesCrimeGuns_Primary') as FormArray;
  }

  get ENESS_XREF_SuccessStoriesCrimeGuns_Related(): FormArray {
    return this.resultForm.get('ENESS_XREF_SuccessStoriesCrimeGuns_Related') as FormArray;
  }

  get ENESS_XREF_SuccessStoriesCIPs(): FormArray {
    return this.resultForm.get('ENESS_XREF_SuccessStoriesCIPs') as FormArray;
  }

  get ENESS_XREF_SuccessStoriesATFAssets(): FormArray {
    return this.resultForm.get('ENESS_XREF_SuccessStoriesATFAssets') as FormArray;
  }

  get ENESS_XREF_SuccessStoriesDefendants(): FormArray {
    return this.resultForm.get('ENESS_XREF_SuccessStoriesDefendants') as FormArray;
  }

  get ENESS_XREF_SuccessStoriesAttachments(): FormArray {
    return this.resultForm.get('ENESS_XREF_SuccessStoriesAttachments') as FormArray;
  }

  get isFileLimitExceeded(): boolean {
    if (this.fileInput) {
      this.fileInput.uploadedFileCount = this.ENESS_XREF_SuccessStoriesAttachments.length;
      let limitExceeded = this.fileInput.isFileLimitExceeded();
      return limitExceeded;
    }

    return false;
  }

  constructor(private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private resultService: ResultsService,
    private productsListService: ProductsListService,
    private agencyService: AgencyService,
    private peopleService: PeopleService,
    private authService: AuthService,
    public locationService: LocationService,
    public lookupService: LookupService,
    private confirmationService: ConfirmationService,
    private leCaseNumberService: LeCaseNumberService,
    private accessibilityService: AccessibilityService,
    private previousRouteService: PreviousRouteService,
    private loaderService: LoaderService,
    private loginService: LoginService,
    private crimeGunService: CrimeGunService,
    public elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef) { }

  ngOnInit(): void {

    this._setTabIndex();

    this.route.paramMap.subscribe((pm: ParamMap) => {
      this._resultId = pm.get('id');

      if (!this._resultId) {
        this.formTitle = AppConstants.Result_FormTitle_Add;
        const transferData: Data = this.previousRouteService.getTransferData() || {};
        if (AppUtils.objectIsEmpty(transferData) || !transferData.hasOwnProperty('ENESS_SuccessStory')) {
          this.resultService.getResultDetailViewModel().pipe(takeUntil(this._unsubscribe$)).subscribe(
            (result: ServerModels.ResultDetailViewModel) => {
              this.resultData = result;
              this._updateFormData();
            });
        } else {
          this.resultData = <ServerModels.ResultDetailViewModel>transferData;
          this._updateFormData();
        }
      } else {
        this.resultService.getResultById(this._resultId).pipe(takeUntil(this._unsubscribe$)).subscribe(
          (result: ServerModels.ResultDetailViewModel) => {
            this.resultData = result;
            this.formTitle = result.ENESS_SuccessStory.CurrentSuccessStoryStatus?.ENESS_REF_SuccessStoriesStatuses?.SuccessStoriesStatus === 'Draft' ? AppConstants.Result_FormTitle_Add : AppConstants.Result_FormTitle_Edit;
            this._updateFormData();
          }
        );
      }
    });

    this.cgicCode = AccountComponent.userInfo.Agency == null ? AccountComponent.userInfo.FieldDivision.ENESS_REF_NIBINCGICCodes.OrganizationCode : AccountComponent.userInfo.Agency.ENESS_REF_FieldDivisions.ENESS_REF_NIBINCGICCodes.OrganizationCode;

  }

  ngOnDestroy(): void {
    this.previousRouteService.transferData = null;
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.accessibilityService.updatePrimeNGFileUploadInput(this.elementRef.nativeElement, 'ResultAttachmentUpload'), 1000);

  }

  private _setTabIndex(): void {
    this._activeTabIndex = Number.parseInt(this.route.snapshot.queryParamMap.get('tabIndex'));

    if (isNaN(this._activeTabIndex)) {
      this._activeTabIndex = 0;
    }
  }

  public onTabChange(evt: TabViewChangeEvent): void {
    if (evt.index == 3 || evt.index == 4) {
      this.setReviewNotifications();
    }
    this.activeTabIndex = evt.index || 0;
    this.router.navigate([], {
      relativeTo: this.route,

      queryParams: {
        ...this.route.snapshot.queryParams,
        tabIndex: this.activeTabIndex,
      }
    });
  }

  public onAddIRTrackingNumber(evt: Event, ctrl: AutoComplete): void {
    if (!ctrl.value) {
      return;
    }

    (<FormArray>this.resultForm.get('ENESS_XREF_SuccessStoriesCIPs')).push(this.fb.group({
      CIPID: ctrl.value.CIPID,
      CIPTrackingID: ctrl.value.CIPTrackingID,
      SuccessStoriesCIPsID: null,
      SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID
    }));
    ctrl.value = ctrl.inputEL.nativeElement.value = null;
    this.irTrackingNumbers = []
  }

  public onAddPrimaryCrimeGunNumber(evt: Event, ctrl: AutoComplete): void {
    if (!ctrl.value)
      return;

    (<FormArray>this.resultForm.get('ENESS_XREF_SuccessStoriesCrimeGuns_Primary')).push(this.fb.group({
      SuccessStoriesCrimeGunsID: null,
      SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID,
      CrimeGunNumberID: ctrl.value.CrimeGunNumberID,
      CrimeGunNumber: ctrl.value.CrimeGunNumber,
      ENESS_CrimeGunNumbers: this.fb.group({ LegacyATFGunNumber: ctrl.value.LegacyATFGunNumber }),
      NumberOfLinkedShootings: ctrl.value.NumberOfLinkedShootings,
      IsPrimary: true
    }));
    ctrl.value = ctrl.inputEL.nativeElement.value = null;
    this.crimeGunNumbers = [];
  }

  public onAddLinkedCrimeGunNumber(evt: Event, ctrl: AutoComplete): void {
    if (!ctrl.value)
      return;

    (<FormArray>this.resultForm.get('ENESS_XREF_SuccessStoriesCrimeGuns_Related')).push(this.fb.group({
      SuccessStoriesCrimeGunsID: null,
      SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID,
      CrimeGunNumberID: ctrl.value.CrimeGunNumberID,
      CrimeGunNumber: ctrl.value.CrimeGunNumber,
      ENESS_CrimeGunNumbers: this.fb.group({ LegacyATFGunNumber: ctrl.value.LegacyATFGunNumber }),
      IsPrimary: false
    }));
    ctrl.value = ctrl.inputEL.nativeElement.value = null;
    this.crimeGunNumbers = [];
  }

  public onAddLECaseNumber() {
    let formData = this.leCaseNumberForm.value;
    let lecn = this.caseNumbersByAgencyId.find((x: ServerModels.ENESS_XREF_LECaseNumbers) => x.LECaseNumberID == formData.LECaseNumberID.LECaseNumberID);
    let state = this.lookupService.usStatesSubj.getValue().find((x: ServerModels.ENESS_REF_State) => x.StateID == formData.StateID);
    let agency = this.agenciesByStateId.find(x => x.AgencyID == formData.AgencyID);
    let cn = this.fb.group({
      SuccessStoryID: null,
      SuccessStoriesLECaseNumbersID: null,
      LECaseNumberID: lecn.LECaseNumberID,
      LECaseNumber: lecn.LECaseNumber,
      StateID: formData.StateID,
      AgencyID: formData.AgencyID,
      ENESS_REF_State: this.fb.group({
        StateAbbreviation: state.StateAbbreviation,
        StateID: state.StateID,
        StateName: state.StateName
      }),
      ENESS_REF_Agency: this.fb.group({
        AgencyID: agency.AgencyID,
        AgencyName: agency.AgencyName
      })
    });

    (<FormArray>this.resultForm.get('ENESS_XREF_SuccessStoriesLECaseNumbers')).push(cn);
    this.leCaseNumberForm.reset({
      StateID: '',
      AgencyID: { value: '', disabled: true },
      LECaseNumberID: { value: '', disabled: true }
    });
  }

  public onDefendantAdded(defendant: ServerModels.ENESS_XREF_SuccessStoriesDefendants): void {
    if (!defendant) {
      return;
    }
    let idx: number = -1;
    let existingDefendant = this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoryDefendant.find((x, i) => {
      idx = i;
      return x.PersonID == defendant.PersonID;
    });
    if (existingDefendant) {
      existingDefendant = Object.assign(existingDefendant, { SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID }, defendant);
      this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoryDefendant[idx] = existingDefendant;
    } else {
      this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoryDefendant = [...this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoryDefendant, defendant];
    }

    this.ENESS_XREF_SuccessStoriesDefendants.patchValue(this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoryDefendant);

    this.showSidebar = false;
  }

  public onFileUpload(event: FileUploadEvent): void {
    if (event.originalEvent['status'] !== 200) {
      return;
    }

    const response = event.originalEvent['body'];
    if (response) {
      for (let i = 0; i < response.length; i++) {
        if (response[i].CreatedOn) {
          response[i].CreatedOn = new Date(parseInt(response[i].CreatedOn.substr(6)));
        }
      }
      let uploadedFiles: ServerModels.ENESS_SuccessStoryAttachments[] = response;

      uploadedFiles.forEach(x => {
        (<FormArray>this.resultForm.get('ENESS_XREF_SuccessStoriesAttachments')).push(this.fb.group({
          SuccessStoryAttachmentID: null,
          SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID,
          AttachmentID: x.SuccessStoryAttachmentID,
          CreatedOn: x.CreatedOn,
          ENESS_SuccessStoryAttachments: this.fb.group(x)
        }));
      });

      this._saveFormData();
    }
  }

  public onSelectAttachment(evt: { originalEvent: Event, files: File[], currentFiles: File[] }): void {
    this.fileInput.msgs = [];
    this.fileInput.uploadedFileCount = this.ENESS_XREF_SuccessStoriesAttachments.length;
    if (evt.files.length > 0) {
      for (let i = 0; i < evt.files.length; i++) {
        let file = evt.files[i];
        if (file.size > environment.maxUploadFileSizeInBytes) {
          this.fileInput.msgs.push({
            severity: 'error',
            summary: '',
            detail: `${file.name} exceeds the maximum file size of 5MB.`
          });
        }
      }
    }
  }

  public onRemoveAttachment(removeEvt: FileRemoveEvent): void {
    if (this.fileInput) {
      this.fileInput.uploadedFileCount = this.ENESS_XREF_SuccessStoriesAttachments.length;
      if (this.fileInput.hasFiles() && ((this.fileInput.uploadedFileCount + this.fileInput.files.length - 1) <= this.fileInput.fileLimit)) {
        this.fileInput.msgs = [];
      }
    }
  }

  public onClickDownload(file: ServerModels.ENESS_SuccessStoryAttachments) {
    if (file) {
      const blob = AppUtils.base64toBlob(file.Attachment, null);
      const link = this.downloadTemplateLink.nativeElement;
      const url = window.URL.createObjectURL(blob);
      link.download = file.FileName;
      link.href = url;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  }

  public onUploadErrorHandler(event: FileUploadErrorEvent): void {
    this.attachmentUploadErrorMessage = event.error.error || event.error.message;
    this.displayAttachmentErrorMessage = true;
    this.fileInput.msgs = [];
    this.fileInput.msgs.push({
      severity: 'error',
      summary: 'Attachmend Upload Error:',
      detail: this.attachmentUploadErrorMessage
    });
  }

  public onChangeHasNFORCECaseNumber(value: string): void {
    this.hideNFORCECaseInput = value !== 'Yes';
    if (value !== 'Yes') {
      this.resultForm.get('NFORCECaseNumber').reset('');
      this.resultForm.get('NFORCECaseNumber').clearValidators();
    } else {
      this.resultForm.get('NFORCECaseNumber').setValidators([Validators.required, NESSValidators.NFORCENumberValidator]);
    }

    this.resultForm.get('NFORCECaseNumber').updateValueAndValidity();
  }

  public onChangeFD(event: Event, cgicCode: string) {
    if (!cgicCode) {
      this.resultData.AllCGICSupervisors = [];
      this.resultData.AllCGICPersonnel = [];
      return;
    }
    forkJoin({
      cgicPersonnel: this.resultService.getCGICPersonnel(cgicCode),
      cgicSupervisors: this.resultService.getSupervisorsByCGIC(cgicCode)
    })
      .pipe(takeUntil(this._unsubscribe$)).subscribe({
        next: (val: { cgicPersonnel: ServerModels.CGICPersonnelViewModel[], cgicSupervisors: ServerModels.CGICPersonnelViewModel[] }) => {
          this.resultData.AllCGICPersonnel = val.cgicPersonnel;
          this.resultData.AllCGICSupervisors = val.cgicSupervisors;
        }
      });
  }

  public onApprove(evt: Event): void {
    this.toggleCommentsValidation(false);

    if (this.resultForm.invalid) {
      return;
    }

    this.newResultStatus = <ServerModels.ENESS_XREF_SuccessStoryStatus>{
      SuccessStoryStatusID: null,
      SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID,
      ApproverID: this.authService.currentUserId,
      StatusID: this.approvedStatusId,
      Comment: this.resultForm.get('StatusNotes').value,
      EditOnly: false
    };

    this.approveOrRejected = "approved";
    this._updateStatus(evt);
  }

  public onReject(evt: Event): void {
    this.toggleCommentsValidation(true);

    if (this.resultForm.invalid) {
      return;
    }

    this.newResultStatus = <ServerModels.ENESS_XREF_SuccessStoryStatus>{
      SuccessStoryStatusID: null,
      SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID,
      ApproverID: this.authService.currentUserId,
      StatusID: this.rejectedStatusId,
      Comment: this.resultForm.get('StatusNotes').value,
      EditOnly: false
    };

    this.approveOrRejected = "rejected";
    this._updateStatus(evt);
  }

  public onSubmit(evt: Event): void {
    if (this.currentResultStatus.ENESS_REF_SuccessStoriesStatuses.SuccessStoriesStatus == AppConstants.Result_Status_Draft || this.currentResultStatus.ENESS_REF_SuccessStoriesStatuses.SuccessStoriesStatus == AppConstants.Result_Status_1stLineRejected) {
      const pending1stLineApprovalStatus = this.resultData.SuccessStoriesStatuses.find(x => x.SuccessStoriesStatus == AppConstants.Result_Status_Pending1stLineApproval);
      this.newResultStatus = <ServerModels.ENESS_XREF_SuccessStoryStatus>{
        SuccessStoryStatusID: null,
        SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID,
        ApproverID: null,
        StatusID: pending1stLineApprovalStatus.SuccessStoriesStatusID,
        Comment: null,
        EditOnly: false
      };
    }

    if (this.currentResultStatus.ENESS_REF_SuccessStoriesStatuses.SuccessStoriesStatus == AppConstants.Result_Status_2ndLineApproved) {
      if (this.resultData.CurrentUserIsFDLeadership && this.resultData.CurrentUserIsApprover && this.authService.isCreator(this.resultData.ENESS_SuccessStory.CreatedBy_Id)) {
        const pending2ndLineApprovalStatus = this.resultData.SuccessStoriesStatuses.find(x => x.SuccessStoriesStatus == AppConstants.Result_Status_Pending2ndLineApproval);
        this.newResultStatus = <ServerModels.ENESS_XREF_SuccessStoryStatus>{
          SuccessStoryStatusID: null,
          SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID,
          ApproverID: null,
          StatusID: pending2ndLineApprovalStatus.SuccessStoriesStatusID,
          Comment: null,
          EditOnly: true
        };
      } else if (this.authService.isCreator(this.resultData.ENESS_SuccessStory.CreatedBy_Id) || this.resultData.CurrentUserIsApprover || this.authService.hasRole(['NESS Administrator'])) {
        const pending1stLineApprovalStatus = this.resultData.SuccessStoriesStatuses.find(x => x.SuccessStoriesStatus == AppConstants.Result_Status_Pending1stLineApproval);
        this.newResultStatus = <ServerModels.ENESS_XREF_SuccessStoryStatus>{
          SuccessStoryStatusID: null,
          SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID,
          ApproverID: null,
          StatusID: pending1stLineApprovalStatus.SuccessStoriesStatusID,
          Comment: null,
          EditOnly: true
        };
      }
    }

    if (this.currentResultStatus.ENESS_REF_SuccessStoriesStatuses.SuccessStoriesStatus == AppConstants.Result_Status_2ndLineRejected) {
      if (this.resultData.CurrentUserIsFDLeadership) {
        const pending2ndLineApprovalStatus = this.resultData.SuccessStoriesStatuses.find(x => x.SuccessStoriesStatus == AppConstants.Result_Status_Pending2ndLineApproval);
        this.newResultStatus = <ServerModels.ENESS_XREF_SuccessStoryStatus>{
          SuccessStoryStatusID: null,
          SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID,
          ApproverID: null,
          StatusID: pending2ndLineApprovalStatus.SuccessStoriesStatusID,
          Comment: null,
          EditOnly: true
        };
      } else if (this.authService.isCreator(this.resultData.ENESS_SuccessStory.CreatedBy_Id) || this.resultData.CurrentUserIsApprover || this.authService.hasRole(['NESS Administrator'])) {
        const pending1stLineApprovalStatus = this.resultData.SuccessStoriesStatuses.find(x => x.SuccessStoriesStatus == AppConstants.Result_Status_Pending1stLineApproval);
        this.newResultStatus = <ServerModels.ENESS_XREF_SuccessStoryStatus>{
          SuccessStoryStatusID: null,
          SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID,
          ApproverID: null,
          StatusID: pending1stLineApprovalStatus.SuccessStoriesStatusID,
          Comment: null,
          EditOnly: true
        };
      }
    }

    this._updateStatus(evt);
  }

  private _updateStatus(evt: Event): void {
    let status: ServerModels.ENESS_REF_SuccessStoriesStatuses;

    this.resultData.SuccessStoriesStatuses.forEach(x => {
      switch ((<HTMLButtonElement>evt.target).value) {
        case 'draft':
          if (x.SuccessStoriesStatus === AppConstants.Result_Status_Draft) {
            this.resultForm.get('SuccessStoryStatusID').setValue(x.SuccessStoriesStatusID);
            status = x;

            if (!this.validateNForceNumber(this.resultForm.get('NFORCECaseNumber').value)) {
              this.resultForm.get('NFORCECaseNumber').reset('');
            }
          }

          break;
        case 'submit':
          if (this.newResultStatus?.StatusID === x.SuccessStoriesStatusID) {
            status = x;
          } else if (this.currentResultStatus.StatusID === x.SuccessStoriesStatusID) {
            status = x;
          }
          break;
        case 'approve':
          if (this.newResultStatus.StatusID == this.approvedStatusId && this.newResultStatus.StatusID == x.SuccessStoriesStatusID) {
            status = x;
          }

          if (this.newResultStatus.StatusID == this.rejectedStatusId && this.newResultStatus.StatusID == x.SuccessStoriesStatusID) {
            status = x;
          }
          break;
      }
    });

    if ((<HTMLButtonElement>evt.target).value === 'draft') {
      this.confirmationService.confirm({
        acceptLabel: 'Ok',
        rejectLabel: 'Cancel',
        message: 'Your Result will be saved as a draft.',
        header: 'Confirmation',
        icon: 'pi pi-info-circle',
        accept: () => {
          this.newResultStatus = <ServerModels.ENESS_XREF_SuccessStoryStatus>{
            SuccessStoryStatusID: null,
            SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID,
            ApproverID: null,
            StatusID: status.SuccessStoriesStatusID,
            Comment: null,
            ENESS_REF_SuccessStoriesStatuses: status,
            EditOnly: false
          };
          this._uploadAttachments();
        },
        reject: () => {
          status = null;
        }
      });
    } else if ((<HTMLButtonElement>evt.target).value === 'approve') {
      this.resultForm.get('SuccessStoryStatusID').setValue(status.SuccessStoriesStatusID);
      this.newResultStatus.ENESS_REF_SuccessStoriesStatuses = status;
      this._uploadAttachments();

    } else if ((<HTMLButtonElement>evt.target).value === 'submit') {
      this.resultForm.get('SuccessStoryStatusID').setValue(status.SuccessStoriesStatusID);
      if (!!this.newResultStatus) {
        this.newResultStatus.ENESS_REF_SuccessStoriesStatuses = status;
      } else {
        this.currentResultStatus.ENESS_REF_SuccessStoriesStatuses = status;
        this.currentResultStatus.EditOnly = true;
      }
      this._uploadAttachments();
    }
  }

  public showPersonDetails(person: ServerModels.ENESS_PersonDTO): void {
    this._getPersonLECases(person.PersonID);
    this._getPersonInfoReports(person.PersonID);
    this._getPersonResults(person.PersonID);
    this.selectedPerson = person;
    this.showPerson = true;
    this.viewPersonDetails = true;
    this.showPersonAddressDetails = false;
    this.showPersonAddressDetailForm = false;
    this.personAddress = null;
  }

  public viewAddressDetails(addressDetails: any[]): void {
    this.viewPersonDetails = false;
    this.showPersonAddressDetails = true;
    this.showPersonAddressDetailForm = false;
    this.hasPermissionToEditAddress = addressDetails[1];
    this.personAddressTypeFromRef = addressDetails[2];
    this.personAddressTypeOther = addressDetails[3];
    this.personAddressType = addressDetails[2] === "Other" ? addressDetails[2] + ": " + addressDetails[3] : addressDetails[2];
    forkJoin({
      address: this.locationService.getAddressDetailsById(addressDetails[0]),
      leCases: this.locationService.getAddressLECases(addressDetails[0]),
      people: this.locationService.getAddressPeople(addressDetails[0])
    }).pipe(take(1)).subscribe({
      next: (val: { address: ServerModels.ENESS_AddressDTO, leCases: ServerModels.AddressLECases[], people: ServerModels.AddressPeople[] }) => {
        this.showLocationView = true;
        this.personAddress = val.address;
        this.locationService.addressLECasesSubj.next(val.leCases);
        this.locationService.addressPeopleSubj.next(val.people);
      }
    })
  }

  public editLocation(editLocation: string): void {
    if (editLocation === 'Edit') {
      this.viewPersonDetails = false;
      this.showPersonAddressDetails = false;
      this.showPersonAddressDetailForm = true;
      this.showLocationView = false;
      this.showEditLocationView = true;
    }
  }

  public onLocationFormClose(newAddress: ServerModels.ENESS_AddressDTO): void {
    this.viewAddressDetails([newAddress.AddressID, true, this.personAddressTypeFromRef, this.personAddressTypeOther]);
  }

  public onViewPersonDetailsClose(): void {
    this.selectedPerson = null;
    this.showPerson = false;
    this.viewPersonDetails = false;
  }

  public onClickCloseButton(): void {
    if (this.viewPersonDetails) {
      this.onViewPersonDetailsClose();
    }
    if (this.showPersonAddressDetails) {
      this.showPersonAddressDetails = false;
      this.viewPersonDetails = true;
      this.personAddress = null;
    }
    if (this.showPersonAddressDetailForm) {
      this.viewAddressDetails([this.personAddress.AddressID, true, this.personAddressTypeFromRef, this.personAddressTypeOther]);
    }
  }

  public searchIRTrackingNumbers(evt: { originalEvent: Event, query: string }): void {
    if (evt.query) {
      this.productsListService.searchIRs(evt.query.toLowerCase()).pipe(takeUntil(this._unsubscribe$)).subscribe(
        (IRs: ServerModels.CIPSelectionValues[]) => this.irTrackingNumbers = IRs
      );
    }
  }

  public searchCrimeGunNumbers(evt: { originalEvent: Event, query: string }): void {
    if (!evt.query) {
      return;
    }

    this.crimeGunService.searchByCrimeGunNumber(evt.query).pipe(takeUntil(this._unsubscribe$)).subscribe((cgs: ServerModels.CrimeGunSelectionValues[]) => this.crimeGunNumbers = [...cgs]);
  }

  public searchAgencies(stateId: string): void {
    if (stateId) {
      this.leCaseNumberForm.get('AgencyID').reset('', { disabled: true });
      this.leCaseNumberForm.get('LECaseNumberID').reset('', { disabled: true });
      this.agencyService.getAgenciesForState(stateId).pipe(takeUntil(this._unsubscribe$)).subscribe(
        (agency: ServerModels.ENESS_REF_Agency[]) => {
          this.caseNumbersByAgencyId.length = 0;
          this.agenciesByStateId.length = 0;
          this.agenciesByStateId = [...agency];
          this._updateLECaseNumberState();
        }
      );
    }
  }

  public searchLECaseNumbers(agencyId: string): void {
    this.leCaseNumberForm.get('LECaseNumberID').reset('', { disabled: true });
    if (agencyId) {
      this.leCaseNumberService.getLECaseNumberByAgency(agencyId).pipe(takeUntil(this._unsubscribe$)).subscribe(
        (cn: ServerModels.ENESS_XREF_LECaseNumbers[]) => {
          this.caseNumbersByAgencyId.length = 0;
          this.caseNumbersByAgencyId = [...cn];
          this._updateLECaseNumberState();
        }
      );
    }
  }

  public filterLECaseNumberSingle(event: { originalEvent: Event, query: string }) {
    let query = event.query;
    let formData = this.resultForm.getRawValue() as ServerModels.ENESS_SuccessStory;
    let leCasesInCurrentResult: ServerModels.ENESS_XREF_SuccessStoriesLECaseNumbers[] = [];
    formData.ENESS_XREF_SuccessStoriesLECaseNumbers.forEach(x => {
      leCasesInCurrentResult.push(x);
    });
    this.caseNumbersTextFiltered = this.leCaseNumberService.filterCaseNumbers(query, this.caseNumbersByAgencyId, leCasesInCurrentResult);
  }

  public deleteUploadedAttachment(itemIndex: number): void {
    let attachment = this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesAttachments[itemIndex].ENESS_SuccessStoryAttachments;
    attachment.Deleted = true;
    this.resultService.deleteResultAttachments(attachment).subscribe(
      (attach: ServerModels.ENESS_SuccessStoryAttachments) => {
        (<FormArray>this.resultForm.get('ENESS_XREF_SuccessStoriesAttachments')).removeAt(itemIndex);
        this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesAttachments.splice(itemIndex, 1);
        this.fileInput.uploadedFileCount = this.ENESS_XREF_SuccessStoriesAttachments.length;
        this.fileInput.msgs = [];
        this.fileInput.checkFileLimit(this.fileInput.files);
        this.fileInput.cd.markForCheck();
      }
    );
  }

  public toggleCommentsValidation(require: boolean): void {
    let statusNotesCtrl = this.resultForm.get('StatusNotes');
    statusNotesCtrl.clearValidators();

    if (require) {
      statusNotesCtrl.setValidators(Validators.required);
    }

    statusNotesCtrl.updateValueAndValidity();
  }

  public viewSelectedDefendant(defendant: ServerModels.ENESS_XREF_SuccessStoriesDefendants): void {
    this.showSidebar = true;
    this.selectedDefendant = defendant;
  }

  private _updateLECaseNumberState(): void {
    if (this.caseNumbersByAgencyId.length == 0) {
      this.leCaseNumberForm.get('LECaseNumberID').disable();
    } else {
      this.leCaseNumberForm.get('LECaseNumberID').enable();
    }

    if (this.agenciesByStateId.length == 0) {
      this.leCaseNumberForm.get('AgencyID').disable();
    } else {
      this.leCaseNumberForm.get('AgencyID').enable();
    }
  }

  private _uploadAttachments() {
    this.displayAttachmentErrorMessage = false;
    if ((this.fileInput.files.length + this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesAttachments.length) > this.totalNumberOfUploadFilesAllowed) {
      this.attachmentUploadErrorMessage = "Total number of uploaded files exceed the max limit.";
      this.displayAttachmentErrorMessage = true;
      return;
    }

    if (this.fileInput.files.length > 0) {
      this.fileInput.upload();
    } else {
      this._saveFormData();
    }
  }

  private _saveFormData(): void {
    let successStoryFormData = this._prepareFormData();
    if (successStoryFormData) {
      this.clearMessages();
      this.resultService.addResult(successStoryFormData).pipe(takeUntil(this._unsubscribe$)).subscribe(
        (result: ServerModels.ResultDetailViewModel) => {
          this.resultData = result;
          if (this.resultData.ENESS_SuccessStory?.CurrentSuccessStoryStatus?.ENESS_REF_SuccessStoriesStatuses?.SuccessStoriesStatus == 'Draft') {
            this.changeDetectorRef.detectChanges();
            this.resultForm.patchValue({
              SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID,
              SuccessStoryStatusID: this.resultData.ENESS_SuccessStory.SuccessStoryStatusID,
              CGICSupervisor_Id: this.resultData.ENESS_SuccessStory.CGICSupervisor_Id || '',
              Supervisor: this.resultData.ENESS_SuccessStory.CGICSupervisor == null ? '' : `${this.resultData.ENESS_SuccessStory.CGICSupervisor.LastName}, ${this.resultData.ENESS_SuccessStory.CGICSupervisor.FirstName}`,
              AlternateCGICApprover_Id: this.resultData.ENESS_SuccessStory.AlternateCGICApprover_Id || '',
            });

            this.router.navigate(['/result-form', { id: result.ENESS_SuccessStory.SuccessStoryID }], { replaceUrl: true, queryParams: { tabIndex: this.activeTabIndex }, queryParamsHandling: "preserve" });
          } else {
            if (this.approveOrRejected != "") {
              let _message = 'Result successfully ' + this.approveOrRejected + '.';
              this.approveOrRejected = "";
              this.confirmationService.confirm({
                acceptLabel: 'Close',
                rejectVisible: false,
                closeOnEscape: false,
                message: _message,
                header: 'Success',
                icon: 'pi pi-info-circle',
                accept: () => {
                  if (!!this._returnUrl) {
                    this.previousRouteService.returnUrl = null;
                    this.router.navigateByUrl(this._returnUrl);
                  } else {
                    this.router.navigate(['/results']);
                  }
                }
              });
            }
            else {
              if (!!this._returnUrl) {
                this.previousRouteService.returnUrl = null;
                this.router.navigateByUrl(this._returnUrl);
              } else {
                this.router.navigate(['/results']);
              }
            }
          }
        },
        (err: any) => this.msgs = [...[{ severity: 'error', summary: '', detail: err.error || err.error.ExceptionMessage }]]
      );
    }
  }

  private _prepareFormData(): ServerModels.ENESS_SuccessStory {
    let isFormDataValid: boolean = false;
    let formData = this.resultForm.getRawValue();
      formData.CreatedOn = this.resultData.ENESS_SuccessStory.CreatedOn;

      const status: string = this.newResultStatus?.ENESS_REF_SuccessStoriesStatuses?.SuccessStoriesStatus || this.currentResultStatus?.ENESS_REF_SuccessStoriesStatuses.SuccessStoriesStatus;

      if (status == AppConstants.Result_Status_Draft) {
        isFormDataValid = !!(this.resultForm.get('CreatedBy').value);
      }

      if (status == AppConstants.Result_Status_1stLineRejected || status == AppConstants.Result_Status_2ndLineRejected) {
        isFormDataValid = !!(this.resultForm.get('StatusNotes').value);
      }

      if (status == AppConstants.Result_Status_Pending1stLineApproval || status == AppConstants.Result_Status_Pending2ndLineApproval || status == AppConstants.Result_Status_2ndLineApproved) {
        isFormDataValid = this.resultForm.valid;
      }

      if (isFormDataValid) {
        formData.ENESS_XREF_SuccessStoriesATFAssets.forEach((x: ServerModels.ENESS_XREF_SuccessStoriesATFAssets) => {
          delete x.ENESS_REF_SuccessStoryATFAssets;
        });
        formData.ENESS_XREF_SuccessStoriesFactors.forEach((x: ServerModels.ENESS_XREF_SuccessStoriesFactors) => {
          delete x.ENESS_REF_SuccessStoriesFactors;
        });
        formData.ENESS_XREF_SuccessStoriesLECaseNumbers.forEach((x: ServerModels.ENESS_XREF_SuccessStoriesLECaseNumbers) => {
          delete x.ENESS_REF_Agency;
          delete x.ENESS_REF_State;
        });
        formData.ENESS_XREF_SuccessStoryDefendant = JSON.parse(JSON.stringify((this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoryDefendant)));
        formData.ENESS_XREF_SuccessStoryDefendant.forEach((x: ServerModels.ENESS_XREF_SuccessStoriesDefendants) => {
          delete x.ENESS_Person;
          delete x.ENESS_REF_JudicialDistricts;
          delete x.ENESS_REF_State;
          delete x.ENESS_REF_SuccessStoryProsecutionTypes;
          delete x.ENESS_REF_SuccessStoryAdjudicationStatuses;
        });
        formData.ENESS_XREF_SuccessStoriesAttachments.forEach((x: ServerModels.ENESS_XREF_SuccessStoriesAttachments) => {
          delete x.ENESS_SuccessStoryAttachments;
        });
      }

      let crimeGuns = formData.ENESS_XREF_SuccessStoriesCrimeGuns_Primary.concat(formData.ENESS_XREF_SuccessStoriesCrimeGuns_Related);

      let updatedFormData: ServerModels.ENESS_SuccessStory = {
        SuccessStoryID: formData.SuccessStoryID,
        SuccessStoryStatusID: this.newResultStatus?.StatusID || this.currentResultStatus?.StatusID,
        InvestigativeTrackingNumber: formData.InvestigativeTrackingNumber,
        CGICSupervisor_Id: formData.CGICSupervisor_Id,
        AlternateCGICApprover_Id: formData.AlternateCGICApprover_Id,
        CreatedBy: formData.CreatedBy,
        CreatedBy_Id: formData.CreatedBy_Id,
        CreatedOn: formData.CreatedOn,
        Narrative: formData.Narrative,
        StatusNotes: formData.StatusNotes,
        NFORCECaseNumber: formData.NFORCECaseNumber,
        HasNFORCECaseNumberID: formData.HasNFORCECaseNumberID,
        ENESS_XREF_SuccessStoriesATFAssets: this._processCheckboxes(formData.ENESS_XREF_SuccessStoriesATFAssets),
        ENESS_XREF_SuccessStoriesFactors: this._processCheckboxes(formData.ENESS_XREF_SuccessStoriesFactors),
        ENESS_XREF_SuccessStoriesLECaseNumbers: formData.ENESS_XREF_SuccessStoriesLECaseNumbers,
        ENESS_XREF_SuccessStoryDefendant: formData.ENESS_XREF_SuccessStoryDefendant,
        ENESS_XREF_SuccessStoriesAttachments: formData.ENESS_XREF_SuccessStoriesAttachments,
        ENESS_XREF_SuccessStoriesCrimeGuns: crimeGuns,
        ENESS_XREF_SuccessStoriesCIPs: formData.ENESS_XREF_SuccessStoriesCIPs,
        NumberOfShootings: formData.NumberOfShootings,
        NumberOfFirearmsRecovered: formData.NumberOfFirearmsRecovered,
        CGICCode: formData.CGICCode,
        CurrentSuccessStoryStatus: this.newResultStatus || this.currentResultStatus
      } as ServerModels.ENESS_SuccessStory;

      return updatedFormData;
    }

  private _processCheckboxes(chbxArray: any[]): any[] {
    let selectedChbxs: any[] = [];
    chbxArray.forEach((x, idx) => {
      if (x.chbx) {
        delete x.chbx;
        selectedChbxs.push(x)
      }
    });

    return selectedChbxs;
  }

  private _updateFormData(): void {
    if (this.ENESS_REF_SuccessStoriesStatuses.SuccessStoriesStatus == AppConstants.Result_Status_Pending1stLineApproval) {
      this.approvalFormTitleText = "1st Line Approval Form";
      this.approvedStatusId = this._getSuccessStoryStatusId(AppConstants.Result_Status_Pending2ndLineApproval);
      this.rejectedStatusId = this._getSuccessStoryStatusId(AppConstants.Result_Status_1stLineRejected);
    }

    if (this.ENESS_REF_SuccessStoriesStatuses.SuccessStoriesStatus == AppConstants.Result_Status_Pending2ndLineApproval) {
      this.approvalFormTitleText = "2nd Line Approval Form";
      this.approvedStatusId = this._getSuccessStoryStatusId(AppConstants.Result_Status_2ndLineApproved);
      this.rejectedStatusId = this._getSuccessStoryStatusId(AppConstants.Result_Status_2ndLineRejected);
    }

    this.showApprovalTab = this.ENESS_REF_SuccessStoriesStatuses.SuccessStoriesStatus !== AppConstants.Result_Status_Draft && (this.ENESS_REF_SuccessStoriesStatuses.SuccessStoriesStatus == AppConstants.Result_Status_Pending1stLineApproval || this.ENESS_REF_SuccessStoriesStatuses.SuccessStoriesStatus == AppConstants.Result_Status_Pending2ndLineApproval);

    this.currentResultStatus = this.resultData.ENESS_SuccessStory.CurrentSuccessStoryStatus;
    this.newResultStatus = null;

    this.ENESS_XREF_SuccessStoriesFactors.clear();
    this.resultData.SuccessStoriesFactors.forEach(f => {
      let idx: number = this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesFactors.findIndex(i => i.SuccessStoryFactorID == f.SuccessStoriesFactorID);

      this.ENESS_XREF_SuccessStoriesFactors.push(this.fb.group({
        chbx: idx > -1,
        SuccessStoriesFactorsID: idx > -1 ? this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesFactors[idx].SuccessStoriesFactorsID : null,
        SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID,
        SuccessStoryFactorID: f.SuccessStoriesFactorID,
        CreatedOn: idx > -1 ? this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesFactors[idx].CreatedOn : null,
        CreatedBy: idx > -1 ? this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesFactors[idx].CreatedBy : null
      }));
    }, this);

    this.ENESS_XREF_SuccessStoriesATFAssets.clear();
    this.resultData.SuccessStoryATFAssets.forEach(a => {
      let idx: number = this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesATFAssets.findIndex(i => i.ATFAssetID == a.ATFAssetID);
      (<FormArray>this.resultForm.get('ENESS_XREF_SuccessStoriesATFAssets')).push(this.fb.group({
        chbx: idx > -1,
        SuccessStoryATFAssetID: idx > -1 ? this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesATFAssets[idx].SuccessStoryATFAssetID : null,
        SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID,
        ATFAssetID: a.ATFAssetID,
        CreatedOn: idx > -1 ? this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesATFAssets[idx].CreatedOn : null,
        CreatedBy: idx > -1 ? this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesATFAssets[idx].CreatedBy : null
      }));
    });

    this.ENESS_XREF_SuccessStoriesCIPs.clear();
    this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesCIPs.forEach(cip => {
      let ir = this.fb.group({
        SuccessStoriesCIPsID: cip.SuccessStoriesCIPsID,
        SuccessStoryID: cip.SuccessStoryID,
        CIPID: cip.CIPID,
        CIPTrackingID: cip.CIPTrackingID,
        CreatedOn: cip.CreatedOn,
        CreatedBy: cip.CreatedBy
      });

      (<FormArray>this.resultForm.get('ENESS_XREF_SuccessStoriesCIPs')).push(ir);
    });

    this.ENESS_XREF_SuccessStoriesCrimeGuns_Primary.clear();
    this.ENESS_XREF_SuccessStoriesCrimeGuns_Related.clear();
    this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesCrimeGuns.forEach(cgn => {

      let cg = this.fb.group({
        SuccessStoriesCrimeGunsID: cgn.SuccessStoriesCrimeGunsID,
        SuccessStoryID: cgn.SuccessStoryID,
        CrimeGunNumberID: cgn.CrimeGunNumberID,
        CrimeGunNumber: cgn.CrimeGunNumber,
        ENESS_CrimeGunNumbers: this.fb.group({
          CrimeGunNumberID: cgn.CrimeGunNumberID,
          LegacyATFGunNumber: (cgn.ENESS_CrimeGunNumbers?.LegacyATFGunNumber || cgn.LegacyATFGunNumber)
        }),
        NumberOfLinkedShootings: cgn.NumberOfLinkedShootings || 0,
        IsPrimary: cgn.IsPrimary,
        CreatedOn: cgn.CreatedOn,
        CreatedBy: cgn.CreatedBy
      });


      if (cgn.IsPrimary) {
        (<FormArray>this.resultForm.get('ENESS_XREF_SuccessStoriesCrimeGuns_Primary')).push(cg);
      } else {
        (<FormArray>this.resultForm.get('ENESS_XREF_SuccessStoriesCrimeGuns_Related')).push(cg);
      }
    });

    this.ENESS_XREF_SuccessStoriesLECaseNumbers.clear()
    this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesLECaseNumbers.forEach(lcn => {
      let lec = this.fb.group({
        SuccessStoriesLECaseNumbersID: lcn.SuccessStoriesLECaseNumbersID,
        SuccessStoryID: lcn.SuccessStoryID,
        LECaseNumberID: lcn.LECaseNumberID,
        LECaseNumber: lcn.LECaseNumber,
        StateID: lcn.StateID,
        AgencyID: lcn.AgencyID,
        CreatedOn: lcn.CreatedOn,
        CreatedBy: lcn.CreatedBy,
        ENESS_REF_State: this.fb.group({
          StateID: lcn.ENESS_REF_State.StateID,
          StateAbbreviation: lcn.ENESS_REF_State.StateAbbreviation
        }),
        ENESS_REF_Agency: this.fb.group({
          AgencyID: lcn.ENESS_REF_Agency.AgencyID,
          AgencyName: lcn.ENESS_REF_Agency.AgencyName
        })
      });

      (<FormArray>this.resultForm.get('ENESS_XREF_SuccessStoriesLECaseNumbers')).push(lec);
    });

    this.ENESS_XREF_SuccessStoriesAttachments.clear()
    this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoriesAttachments.forEach(att => {
      let a = this.fb.group({
        SuccessStoryAttachmentID: att.SuccessStoryAttachmentID,
        SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID,
        AttachmentID: att.AttachmentID,
        CreatedOn: att.CreatedOn,
        CreatedBy: att.CreatedBy,
        ENESS_SuccessStoryAttachments: this.fb.group(att.ENESS_SuccessStoryAttachments ?? {})
      });
      (<FormArray>this.resultForm.get('ENESS_XREF_SuccessStoriesAttachments')).push(a);
    });

    this.ENESS_XREF_SuccessStoriesDefendants.clear()
    this.resultData.ENESS_SuccessStory.ENESS_XREF_SuccessStoryDefendant.forEach(defendant => {
      let fg: FormGroup = this.fb.group({
        SuccessStoryPeopleID: defendant.SuccessStoryPeopleID,
        SuccessStoryID: defendant.SuccessStoryID,
        PersonID: defendant.PersonID,
        SuccessStoryProsecutionTypeID: defendant.SuccessStoryProsecutionTypeID,
        JudicialDistrictID: defendant.JudicialDistrictID,
        StateID: defendant.StateID,
        ArrestDate: defendant.ArrestDate,
        ConvictionDate: defendant.ConvictionDate,
        SentenceInMonths: defendant.SentenceInMonths,
        SentencingDate: defendant.SentencingDate,
        Charges: defendant.Charges,
        SuccessStoryAdjudicationStatusID: [defendant.SuccessStoryAdjudicationStatusID, Validators.required]
      });

      this.ENESS_XREF_SuccessStoriesDefendants.push(fg);
    });

    this.resultForm.patchValue({
      SuccessStoryID: this.resultData.ENESS_SuccessStory.SuccessStoryID,
      SuccessStoryStatusID: this.resultData.ENESS_SuccessStory.SuccessStoryStatusID,
      CreatorName: `${this.resultData.ENESS_SuccessStory.CreatedByUser.LastName}, ${this.resultData.ENESS_SuccessStory.CreatedByUser.FirstName}`,
      CreatedBy: this.resultData.ENESS_SuccessStory.CreatedBy,
      CreatedBy_Id: this.resultData.ENESS_SuccessStory.CreatedBy_Id,
      CreatedOn: this.resultData.ENESS_SuccessStory.CreatedOn,
      Supervisor: this.authService.hasRole(['NESS Administrator']) || this.resultData.ENESS_SuccessStory.CGICSupervisor == null ? '' : `${this.resultData.ENESS_SuccessStory.CGICSupervisor.LastName}, ${this.resultData.ENESS_SuccessStory.CGICSupervisor.FirstName}`,
      AlternateCGICApprover_Id: this.resultData.ENESS_SuccessStory.AlternateCGICApprover_Id || '',
      InvestigativeTrackingNumber: this.resultData.ENESS_SuccessStory.InvestigativeTrackingNumber || '',
      HasNFORCECaseNumberID: this.resultData.ENESS_SuccessStory.HasNFORCECaseNumberID,
      NFORCECaseNumber: this.resultData.ENESS_SuccessStory.NFORCECaseNumber,
      Narrative: this.resultData.ENESS_SuccessStory.Narrative,
      StatusNotes: null,
      NumberOfShootings: this.resultData.ENESS_SuccessStory.NumberOfShootings,
      NumberOfFirearmsRecovered: this.resultData.ENESS_SuccessStory.NumberOfFirearmsRecovered,
      CGICCode: this.resultData.ENESS_SuccessStory.CGICCode == null ? this.cgicCode : this.resultData.ENESS_SuccessStory.CGICCode
    });

    if (!this._resultId) {
      this.resultForm.patchValue({
        CGICSupervisor_Id: this.authService.hasRole(['NESS Administrator']) ? '' : this.resultData.ENESS_SuccessStory.CGICSupervisor_Id || ''
      });
    } else {
      this.resultForm.patchValue({
        CGICSupervisor_Id: this.resultData.ENESS_SuccessStory.CGICSupervisor_Id || ''
      });
    }

    if (this.resultData.ENESS_SuccessStory.ENESS_REF_SuccessStoryDefendantHasNFORCECaseNumber) {
      this.onChangeHasNFORCECaseNumber(this.resultData.ENESS_SuccessStory.ENESS_REF_SuccessStoryDefendantHasNFORCECaseNumber.HasNFORCECaseNumberValue);
    } else {
      this.hideNFORCECaseInput = true;
    }
    this.resultForm.updateValueAndValidity({ onlySelf: true, emitEvent: false });

    if (this.activeTabIndex === 3 || this.activeTabIndex === 4) {
      // allow for change detection to complete on page reload
      setTimeout(() => {
        this.setReviewNotifications();
      });
    }
  }

  private validateNForceNumber(elementValue: string): boolean {
    let _pattern = /^[0-9]{6}\-?[0-9]{2}\-?[0-9]{4}$/;
    return _pattern.test(elementValue);
  }

  public valueRangeCheck(): void {
    if (this.resultForm.get('NumberOfShootings').value > 999) {
      this.resultForm.get('NumberOfShootings').setValue(999, { onlySelf: true, emitEvent: false });
    }
    if (this.resultForm.get('NumberOfShootings').value < 0) {
      this.resultForm.get('NumberOfShootings').setValue(0, { onlySelf: true, emitEvent: false });
    }
    if (this.resultForm.get('NumberOfFirearmsRecovered').value > 999) {
      this.resultForm.get('NumberOfFirearmsRecovered').setValue(999, { onlySelf: true, emitEvent: false });
    }
    if (this.resultForm.get('NumberOfFirearmsRecovered').value < 0) {
      this.resultForm.get('NumberOfFirearmsRecovered').setValue(0, { onlySelf: true, emitEvent: false });
    }
  }

  public activateTab(idx: number) {
    if (idx == 3) {
      this.setReviewNotifications();
    }
    this.onTabChange({ originalEvent: null, index: idx });
  }

  public gotoNextTab() {
    if (this.activeTabIndex == 2) {
      this.setReviewNotifications();
    }
    let tabIndex = this.activeTabIndex + 1;
    this.onTabChange({ originalEvent: null, index: tabIndex });
  }

  public gotoPrevTab() {
    let tabIndex = this.activeTabIndex - 1;
    this.onTabChange({ originalEvent: null, index: tabIndex });
  }

  public clearMessages() {
    this.msgs = [];
  }

  private _getSuccessStoryStatusId(status: string): string {
    let successStatusId: string = null;

    if (!!status) {
      successStatusId = this.resultData.SuccessStoriesStatuses.find(x => x.SuccessStoriesStatus === status)?.SuccessStoriesStatusID;
    }

    return successStatusId;
  }

  private setReviewNotifications() {
    let updatedData = this._prepareFormData();
    this.generalNotification = { header: 'General information', message: '', NotificationType: NotificationType.default, TabIndex: 0 };
    this.narrativeNotification = { header: 'Narrative/Attachments information', message: '', NotificationType: NotificationType.default, TabIndex: 1 };
    this.defendantsNotification = { header: 'Defendant(s) information', message: '', NotificationType: NotificationType.default, TabIndex: 2 };
    this.fileInput.uploadedFileCount = updatedData.ENESS_XREF_SuccessStoriesAttachments.length;

    if (!updatedData.CGICSupervisor_Id || !updatedData.CGICCode) {
      this.generalNotification = { header: 'General information', message: 'Please review missing general information.', NotificationType: NotificationType.alert, TabIndex: 0 };
    }
    if (updatedData.ENESS_XREF_SuccessStoriesFactors.length == 0 || updatedData.NumberOfShootings == null || updatedData.NumberOfShootings === "" || updatedData.NumberOfFirearmsRecovered == null || updatedData.NumberOfFirearmsRecovered === "" || !updatedData.Narrative || this.displayAttachmentErrorMessage) {
      this.narrativeNotification = { header: 'Narrative/Attachments information', message: 'Please review missing narrative/attachments information. ', NotificationType: NotificationType.alert, TabIndex: 1 };
    }
    if (this.fileInput.isFileLimitExceeded()) {
      this.narrativeNotification.NotificationType = NotificationType.alert;
      this.narrativeNotification.message += 'Please review attachments exceeds maximum file upload limit.';
    }

    this.onChangeHasNFORCECaseNumber(this.resultData.SuccessStoryDefendantHasNFORCECaseNumbers.find(x => x.HasNFORCECaseNumberID == updatedData.HasNFORCECaseNumberID)?.HasNFORCECaseNumberValue);
    if (!updatedData.HasNFORCECaseNumberID || !this.resultForm.get('NFORCECaseNumber').valid || this.ENESS_XREF_SuccessStoriesDefendants.invalid) {
      this.defendantsNotification = { header: 'Defendant(s) information', message: 'Please review missing defendants information.', NotificationType: NotificationType.alert, TabIndex: 2 };
    }
  }

  private _getPersonResults(personId: string): void {
    if (AppPermissions.canViewResults) {
      this.peopleService.getPersonResults(personId).pipe(takeUntil(this._unsubscribe$)).subscribe((result: { 'InvestigativeTrackingNumber': string, 'CreatedOn': Date }[]) => this.peopleService.personResultsSubj.next(result));
    }
  }

  private _getPersonInfoReports(personId: string): void {
    if (AppPermissions.canViewIRs) {
      this.peopleService.getPersonInfoReports(personId).pipe(takeUntil(this._unsubscribe$)).subscribe((result: { 'IntelligenceTrackingNumber': string, 'CreatedOn': Date }[]) => this.peopleService.personInfoReportsSubj.next(result));
    }
  }

  private _getPersonLECases(personId: string): void {
    this.peopleService.getPersonLECases(personId).pipe(takeUntil(this._unsubscribe$)).subscribe((result: ServerModels.PersonLECases[]) =>
      this.peopleService.personLECasesSubj.next(result));
  }
}
